// Aggregates the site's areas (which are nested under floors) into one
// flat list + an id→area lookup. Logical-group members reference areas by
// id, so the picker and the member list both need this. One floors query
// + one areas query per floor (fine for a house-sized site).

import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import { areas as areasApi } from "../../api/endpoints/areas";
import { floors as floorsApi } from "../../api/endpoints/floors";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import type { AreaOut } from "../../api/types/area";

export interface SiteArea extends AreaOut {
  floorSlug: string;
  floorLabel: string;
}

export interface SiteAreas {
  items: SiteArea[];
  byId: Map<string, SiteArea>;
  loading: boolean;
  error: unknown;
}

export function useSiteAreas(
  accountSlug: string,
  tenantSlug: string,
  siteSlug: string,
): SiteAreas {
  const floorsQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.floors(accountSlug, tenantSlug, siteSlug),
    queryFn: () =>
      floorsApi.list(accountSlug, tenantSlug, siteSlug).then((r) => r.data),
  });

  const fls = useMemo(() => floorsQ.data ?? [], [floorsQ.data]);

  const areaQs = useQueries({
    queries: fls.map((f) => ({
      ...adminQueryOptions,
      queryKey: qk.areas(accountSlug, tenantSlug, siteSlug, f.slug),
      queryFn: () =>
        areasApi
          .list(accountSlug, tenantSlug, siteSlug, f.slug)
          .then((r) => r.data),
    })),
  });

  // Only the (expensive) flatMap + Map are memoised, keyed on a data
  // snapshot. loading/error are cheap and computed fresh every render so an
  // error or pending transition can never be masked by a stale memo.
  const dataSig = areaQs.map((q) => q.dataUpdatedAt).join(",");
  const items: SiteArea[] = useMemo(
    () =>
      fls.flatMap((f, i) =>
        (areaQs[i]?.data ?? []).map((a) => ({
          ...a,
          floorSlug: f.slug,
          floorLabel: f.label,
        })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fls, dataSig],
  );
  const byId = useMemo(
    () => new Map(items.map((it) => [it.id, it])),
    [items],
  );

  const loading = floorsQ.isPending || areaQs.some((q) => q.isPending);
  const error =
    floorsQ.error ?? areaQs.find((q) => q.error)?.error ?? null;

  return { items, byId, loading, error };
}
